import { useEffect, useRef, useState } from "react";
import { Eraser, PenLine, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function SignatureBox({ token, onSigned }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    // set intrinsic size for crisp lines
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = rect.height * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.strokeStyle = "#141210";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - r.left, y: p.clientY - r.top };
  };
  const start = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasStrokes(true);
  };
  const end = () => setDrawing(false);

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasStrokes(false);
  };

  const submit = async () => {
    if (!name.trim() || name.trim().length < 2) {
      toast.error("Inserisci il tuo nome completo");
      return;
    }
    if (!hasStrokes) {
      toast.error("Firma nel riquadro per confermare");
      return;
    }
    setSending(true);
    try {
      // Composite: draw on white background so PNG isn't transparent
      const src = canvasRef.current;
      const off = document.createElement("canvas");
      off.width = src.width;
      off.height = src.height;
      const ox = off.getContext("2d");
      ox.fillStyle = "#F7F4EE";
      ox.fillRect(0, 0, off.width, off.height);
      ox.drawImage(src, 0, 0);
      const dataUrl = off.toDataURL("image/png");
      const { data } = await api.post(`/quote-requests/public/${token}/sign`, {
        full_name: name.trim(),
        signature_png: dataUrl,
      });
      toast.success("Preventivo firmato ✦");
      onSigned && onSigned(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="av-card p-6 mt-8" data-testid="signature-box">
      <div className="av-eyebrow">Firma digitale</div>
      <h3 className="font-serif-display text-2xl mt-2">
        Firma il preventivo online
      </h3>
      <p className="text-sm text-[#A39E93] mt-1">
        Nessuna email di conferma: la tua firma qui ha valore di accettazione.
      </p>
      <label className="av-label mt-5">Nome e cognome del firmatario</label>
      <input
        className="av-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Es. Mario Rossi"
        data-testid="signature-name-input"
      />
      <label className="av-label mt-4">Firma nel riquadro</label>
      <div className="bg-[#F7F4EE] rounded-lg border av-hairline overflow-hidden touch-none">
        <canvas
          ref={canvasRef}
          className="w-full h-40 cursor-crosshair block"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid="signature-canvas"
        />
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <button
          onClick={clear}
          className="av-pill av-pill-ghost text-xs"
          data-testid="signature-clear"
        >
          <Eraser size={12} /> Pulisci
        </button>
        <button
          onClick={submit}
          disabled={sending}
          className="av-pill av-pill-gold text-sm ml-auto"
          data-testid="signature-submit"
        >
          {sending ? (
            "Firmo…"
          ) : (
            <>
              <PenLine size={14} /> Firma e conferma
            </>
          )}
        </button>
      </div>
    </div>
  );
}
